import React, { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { makeStyles } from '@material-ui/core/styles';
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Box,
  Typography,
  CircularProgress
} from '@material-ui/core';
import { ArrowDropDown } from '@material-ui/icons';
import DefaultScrollWithHeaderContent from "../../../../utils/DefaultScrollWithHeaderContent";
import MyTable from '../../../../utils/MyTable';
import { unoTheme } from "../../../../../assets/styles/unoTheme";
import { ComDinheiroAPI } from "../../../../../API/comdinheiro-api";
import { LocalBenchMarksAPI } from "../../../../../API/local_benchmaks";
import { getDayMonthsAgo, getLastDayInMonthByMonthAndYear} from "../../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../../../actions";
import { HistoricalIndexesAPI } from "../../../../../API/historical_indexes";

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    overflowY: 'auto'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: unoTheme.darkText,
    marginBottom: '12px',
    marginTop: '16px',
    paddingLeft: '12px',
    '&:first-of-type': {
      marginTop: 0
    }
  },
  tableContainer: {
    marginBottom: '20px',
    width: '100%'
  },
  tableColumn: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer'
  },
  positiveValue: {
    color: '#4caf50',
    fontWeight: 500,
  },
  negativeValue: {
    color: '#f44336',
    fontWeight: 500,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: '300px'
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '24px',
    color: '#f44336',
    textAlign: 'center'
  },
});

// Função para classificar benchmark como renda fixa ou renda variável
const classifyBenchmark = (comdinheiro_name) => {
  // Renda Variável (Equity)
  const equityKeywords = [
    'IBRX',
    'IBRX50',
    'IBOV',
    'BDRX',
    'US:SP500',
    'US:MSCI_WORLD',
    'IDIV',
    'IFNC',
    'ICON',
    'SMLL',
    'IFIX',
  ];

  const fixedIncomeKeywords = [
    'ANBIMA_IMAB',
    'ANBIMA_IMAB5',
    'ANBIMA_IMAB5+',
    'ANBIMA_IRFM',
    'ANBIMA_IRFM1',
    'ANBIMA_IRFM1+',
    'ANBIMA_IMAGexC',
    'ANBIMA_IMAG',
    'ANBIMA_IMAS',
    'SELIC',
    'CDI',
    'ANBIMA_IDKA_IPCA_2A',
    'ANBIMA_IDKA_PRE_2A',
    'IPCAdp',
  ];

  const name = comdinheiro_name.toUpperCase();

  const isEquity = equityKeywords.some(keyword => name.includes(keyword.toUpperCase()));
  const isFixedIncome = fixedIncomeKeywords.some(keyword => name.includes(keyword.toUpperCase()));

  if (isEquity) return 'equity';
  if (isFixedIncome) return 'fixed_income';

  // Por padrão, se não conseguir classificar, retorna fixed_income
  return 'other';
};

const getValueColor = (value) => {
  if (value === '-' || value === '—') return '';
  const numValue = parseFloat(value.replace(',', '.').replace('%', ''));
  if (numValue > 0) return 'positiveValue';
  if (numValue < 0) return 'negativeValue';
  return '';
};

// Função para formatar percentual
const formatPercent = (value) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const formatted = (value >= 0 ? '+' : '') + value.toFixed(2).replace('.', ',') + '%';
  return formatted;
};

// Função para calcular retorno acumulado de um array de rentabilidades mensais
const calculateAccumulatedReturn = (monthlyReturns) => {
  if (!monthlyReturns || monthlyReturns.length === 0) return null;

  // Filtrar valores válidos
  const validReturns = monthlyReturns.filter(r => r !== null && r !== undefined && !isNaN(r));

  if (validReturns.length === 0) return null;

  // Calcular retorno acumulado: (1 + r1) * (1 + r2) * ... * (1 + rn) - 1
  let accumulated = 1;
  validReturns.forEach(monthReturn => {
    // Os valores vêm em percentual (ex: 1.84), então divide por 100
    accumulated *= (1 + (monthReturn / 100));
  });

  return (accumulated - 1) * 100;
};

const BenchmarkTab = forwardRef((props, ref) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const clientOn = useSelector(state => state.clientOn);
  const [fixedIncomeData, setFixedIncomeData] = useState([]);
  const [equityData, setEquityData] = useState([]);
  const [innerLoad, setInnerLoad] = useState({
    emptyMessage: "",
    loadMessage: "Carregando índices de referência...",
    size: "small"
  })
  const [error, setError] = useState(null);
  const [allBenchmarks, setAllBenchmarks] = useState([]);

  const loadBenchmarkData = async () => {
    try {
      setError(null);
      // Carregar todos os benchmarks do banco
      const responseBenchmarks = await LocalBenchMarksAPI.getLocalBenchmarks();
      if (!responseBenchmarks.success || !responseBenchmarks.body.rows) {
        setError('Erro ao carregar lista de benchmarks');
        setInnerLoad(null);
        return;
      }
      const allBenchs = responseBenchmarks.body.rows;
      setAllBenchmarks(allBenchs);

      // Obter data de referência (últimos 60 meses)
      const endDate = getLastDayInMonthByMonthAndYear(clientOn.client.selectedPortfolioMonth, clientOn.client.selectedPortfolioYear);
      const startDate = getDayMonthsAgo(endDate, 61);

      // Buscar dados de Renda Fixa

      setAllBenchmarks(allBenchs);

      const allBenchCods = allBenchs.map(b => b.name);

      /////////////////////////////////////////////////////////////////////////
      //Dados devem ser inicialmente buscados localmente para evitar múltiplas requisições a comdinheiro
      /////////////////////////////////////////////////////////////////////////

      const localPeriodCod = (startDate + "_" + endDate).trim();
      const localResponseFixedIncome = await HistoricalIndexesAPI.getHistoricalIndexesByBenchmarksAndPeriod(
        allBenchCods,
        localPeriodCod
      );

      let localInfosFixedIncome = null;
      if (localResponseFixedIncome.success && localResponseFixedIncome.body.rowCount == 1) {
        localInfosFixedIncome = JSON.parse(localResponseFixedIncome.body.rows[0].infos);
      }

      const responseFixedIncome = localInfosFixedIncome ?
        { success: true, body: localInfosFixedIncome } : await ComDinheiroAPI.getHistoricoRentabilidadesPorPeriodo(
          [],
          allBenchs,
          startDate,
          endDate
        );

      /////////////////////////////////////////////////////////////////////////
      //Salvar dados localmente para evitar múltiplas requisições a comdinheiro
      /////////////////////////////////////////////////////////////////////////
      if (!localInfosFixedIncome) {
        await HistoricalIndexesAPI.save(
          allBenchCods,
          localPeriodCod,
          responseFixedIncome.body
        );
      }

      if (!responseFixedIncome.success || !responseFixedIncome.body) {
        setError('Erro ao carregar dados de rentabilidade');
        setInnerLoad(null);
        return;
      }

      const historicalData = responseFixedIncome.body;
      // Separar benchmarks em renda fixa e variável
      const fixedIncomeProcessed = [];
      const equityProcessed = [];

      allBenchs.forEach(benchmark => {
        const benchmarkType = classifyBenchmark(benchmark.comdinheiro_name);

        // Calcular cada período
        const currentMonth = historicalData.at(-1)?.[benchmark.comdinheiro_name] ?? null;
        const noAno = calculateAccumulatedReturn(historicalData.slice(-clientOn.client.selectedPortfolioMonth).map(d => d[benchmark.comdinheiro_name]));
        const tres = calculateAccumulatedReturn(historicalData.slice(-3).map(d => d[benchmark.comdinheiro_name]));
        const seis = calculateAccumulatedReturn(historicalData.slice(-6).map(d => d[benchmark.comdinheiro_name]));
        const doze = calculateAccumulatedReturn(historicalData.slice(-12).map(d => d[benchmark.comdinheiro_name]));
        const vinte4 = calculateAccumulatedReturn(historicalData.slice(-24).map(d => d[benchmark.comdinheiro_name]));
        const trinta6 = calculateAccumulatedReturn(historicalData.slice(-36).map(d => d[benchmark.comdinheiro_name]));
        const quarenta8 = calculateAccumulatedReturn(historicalData.slice(-48).map(d => d[benchmark.comdinheiro_name]));
        const sessenta = calculateAccumulatedReturn(historicalData.slice(-60).map(d => d[benchmark.comdinheiro_name]));

        const dataItem = {
          name: benchmark.name,
          currentMonth: formatPercent(currentMonth),
          currentYear: formatPercent(noAno),
          three: formatPercent(tres),
          six: formatPercent(seis),
          twelve: formatPercent(doze),
          twentyFour: formatPercent(vinte4),
          thirtyFour: formatPercent(trinta6),
          fortyEight: formatPercent(quarenta8),
          sixty: formatPercent(sessenta)
        };

        // Separar por tipo
        if (benchmarkType === 'other') return;

        if (benchmarkType === 'fixed_income') {
          fixedIncomeProcessed.push(dataItem);
        } else if (benchmarkType === 'equity') {
          equityProcessed.push(dataItem);
        }
      });

      setFixedIncomeData(fixedIncomeProcessed);
      setEquityData(equityProcessed);
      setInnerLoad(null);
    } catch (err) {
      console.error('Erro ao carregar dados de benchmark:', err);
      setError('Erro ao carregar dados de benchmark');
      setInnerLoad(null);
    }
  };

  useEffect(() => {
    if (clientOn && clientOn.client
      && clientOn.client.selectedPortfolioMonth && clientOn.client.selectedPortfolioYear) {

      setInnerLoad({
        emptyMessage: "",
        loadMessage: "Carregando índices de referência...",
        size: "small"
      });
      setFixedIncomeData([]);
      setEquityData([]);
      loadBenchmarkData();
    }
  }, [clientOn]);

  const makeReport = () => {
    const reportType = 'benchmarkIndexes';
    dispatch(allActions.reportLoadActions.setReportLoad({
      show: true,
      reportType,
      message: 'Gerando relatório de benchmarks...',
      reportData: {
        fixedIncomeData,
        equityData
      }
    }));
  };

  useImperativeHandle(ref, () => ({
    print: () => window.print(),
    doPdfReport: () => {
      makeReport();
    }
  }));

  const TableBenchmark = ({ title, data }) => (
    <div className={classes.tableContainer}>
      <MyTable stickyHeader size="small">
        <TableHead>
          <TableRow className="smallPadding mainColor">
            <TableCell align="center" colSpan={10}>
              {title}
            </TableCell>
          </TableRow>
          <TableRow className="smallPadding grayColor">
            <TableCell style={{ textAlign: 'left', fontWeight: 500, paddingLeft: '12px' }}>Indexador</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>Mês</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>Ano</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>3 meses</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>6 meses</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>12 meses</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>24 meses</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>36 meses</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>48 meses</TableCell>
            <TableCell style={{ textAlign: 'center', fontWeight: 500 }}>60 meses</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow key={index} hover>
              <TableCell style={{ textAlign: 'left', paddingLeft: '12px' }}>{row.name}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.currentMonth) === 'positiveValue' ? '#4caf50' : getValueColor(row.currentMonth) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.currentMonth}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.currentYear) === 'positiveValue' ? '#4caf50' : getValueColor(row.currentYear) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.currentYear}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.three) === 'positiveValue' ? '#4caf50' : getValueColor(row.three) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.three}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.six) === 'positiveValue' ? '#4caf50' : getValueColor(row.six) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.six}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.twelve) === 'positiveValue' ? '#4caf50' : getValueColor(row.twelve) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.twelve}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.twentyFour) === 'positiveValue' ? '#4caf50' : getValueColor(row.twentyFour) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.twentyFour}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.thirtyFour) === 'positiveValue' ? '#4caf50' : getValueColor(row.thirtyFour) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.thirtyFour}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.fortyEight) === 'positiveValue' ? '#4caf50' : getValueColor(row.fortyEight) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.fortyEight}</TableCell>
              <TableCell align="center" style={{ color: getValueColor(row.sixty) === 'positiveValue' ? '#4caf50' : getValueColor(row.sixty) === 'negativeValue' ? '#f44336' : 'inherit' }}>{row.sixty}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MyTable>
    </div>
  );

  if (error) {
    return (
      <DefaultScrollWithHeaderContent innerLoad={null}>
        <Box className={classes.errorContainer}>
          <Typography>{error}</Typography>
        </Box>
      </DefaultScrollWithHeaderContent>
    );
  }

  return (
    <DefaultScrollWithHeaderContent innerLoad={innerLoad}>
      {allBenchmarks != null && fixedIncomeData.length != 0 && equityData.length != 0 ?
        <Box className={classes.root}>
          <TableBenchmark title="Renda Fixa" data={fixedIncomeData} />
          <TableBenchmark title="Renda Variável" data={equityData} />
        </Box>
        : null
      }


    </DefaultScrollWithHeaderContent>
  );
});

BenchmarkTab.displayName = 'BenchmarkTab';
export default BenchmarkTab;